import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/entreprise/Chauffeur-pour-entreprise.png";

const sophiaAntipolis: ChauffeurPage = {
  slug: "sophia-antipolis",
  title: "VTC & private chauffeur in Sophia Antipolis",
  subtitle:
    "Corporate chauffeur in Sophia Antipolis: executive transfers, multi-site meetings and conference shuttles in the technology park.",
  keywords: [
    "vtc sophia antipolis",
    "private chauffeur sophia antipolis",
    "corporate chauffeur sophia antipolis",
  ],
  hero: {
    title: "VTC & private chauffeur in Sophia Antipolis",
    subtitle:
      "Europe's largest technology park: a corporate chauffeur for executives, delegations and conferences.",
    keywords: [
      "VTC Sophia Antipolis",
      "corporate chauffeur Sophia Antipolis",
      "conference shuttle technology park",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Sophia Antipolis",
    badges: [
      "Executive transfers",
      "Multi-site meetings",
      "Corporate invoicing",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Sophia Antipolis",
    items: [
      {
        title: "Executive transfers",
        description:
          "Meet-and-greet for executives and senior staff at Nice airport, flight tracking and a direct ride to the park.",
      },
      {
        title: "A sprawling park",
        description:
          "Head offices, R&D centres and business parks spread across a vast wooded plateau: a chauffeur who knows every address.",
      },
      {
        title: "Multi-site meetings",
        description:
          "A chain of appointments through the day between Sophia, Antibes, Cannes and Nice, with no lost time or parking.",
      },
      {
        title: "A professional standard",
        description:
          "Discretion, punctuality and corporate invoicing for frictionless business travel.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Sophia Antipolis",
    services: [
      {
        title: "Executive airport transfers",
        text: "Sophia Antipolis ↔ Nice airport with flight tracking and a name-board welcome, for executives, senior staff and delegations.",
      },
      {
        title: "Multi-site meetings",
        text: "A chauffeur on hand to chain appointments between the park's sites, Antibes, Cannes and Nice.",
      },
      {
        title: "Conference shuttles",
        text: "Moving speakers and attendees between hotels, convention centres and seminar venues, with group coordination.",
      },
      {
        title: "Hourly & daily hire",
        text: "A dedicated vehicle and chauffeur for a half-day or full day, at the pace of your business agenda.",
      },
      {
        title: "Roadshows & delegations",
        text: "Planned itineraries for site visits, investors and partners, with clear corporate invoicing.",
      },
    ],
  },
  locations: {
    heading: "Sites & pick-up points",
    items: [
      {
        name: "Heart of the technology park",
        description:
          "Head offices, R&D and business parks on the Valbonne plateau.",
      },
      {
        name: "Business hotels",
        description: "Seminar venues and executive residences in the area.",
      },
      {
        name: "Convention & seminar centres",
        description: "Conference venues and event spaces of the park.",
      },
      {
        name: "Valbonne",
        description:
          "Village and residential edges bordering the technology park.",
      },
      {
        name: "Antibes",
        description:
          "Neighbouring town, station and hotels minutes from the park.",
      },
      {
        name: "Nice airport",
        description: "About 25 minutes via the A8, welcome at both terminals.",
      },
    ],
  },
  generic: {
    heading: "Sophia Antipolis, the Riviera's business technology park",
    subheading:
      "Europe's largest technology park, where business travel demands reliability and punctuality.",
    paragraphs: [
      "Sophia Antipolis gathers head offices, R&D centres and business parks across a vast wooded plateau between Antibes and Valbonne. The sites are scattered and poorly served without a vehicle: a chauffeur who knows every address saves valuable time for visiting executives and delegations.",
      "About 25 minutes from Nice airport via the A8, the technology park is a frequent arrival point for business travellers. Name-board welcome, flight tracking and a direct ride to the office or hotel: the executive transfer starts the day without a hitch.",
      "Conferences, seminars and roadshows punctuate life at Sophia. For these formats, we provide coordinated shuttles between hotels, convention centres and head offices, along with hourly hire by the day to chain multi-site meetings as far as Cannes or Nice. Corporate invoicing is clear and tailored.",
      "Our rates are public, which remains rare in this market. Hourly hire starts at €240 for 3 hours in E-Class and €450 in S-Class; a four-hour half day costs €320 in E-Class, €480 in V-Class and €600 in S-Class. Past the package, each extra hour is billed between €80 and €150 depending on the class. The Sophia Antipolis ⇄ Nice airport transfer follows the Riviera fixed-price grid, and any journey outside it is quoted from €2.60/km.",
    ],
    bullets: [
      "Hourly hire from €240 (3 h) and €320 per half day",
      "Executive flight tracking, name-board welcome at both terminals",
      "Multi-site meetings across Sophia, Antibes, Cannes and Nice",
      "Corporate invoicing, registered company since 2022",
    ],
  },
  process: {
    heading: "Book a chauffeur in Sophia Antipolis",
    steps: [
      "Send your programme (flight, sites to visit, conference, meeting times).",
      "We plan the itinerary and the hourly hire around your business agenda.",
      "The chauffeur greets you at the airport or appears at the site given.",
      "Direct transfers or a full multi-site day, with corporate invoicing.",
    ],
  },
  faq: [
    {
      q: "How long is it from Nice airport to Sophia Antipolis?",
      a: "Around 25 minutes via the A8 outside peak hours. We track the flights of executives and delegations to adjust the pick-up and hold the day's agenda.",
    },
    {
      q: "Can you handle meetings across several sites in one day?",
      a: "Yes. Hourly hire of a chauffeur lets you chain appointments between the park's sites, Antibes, Cannes and Nice, with no lost time or search for parking.",
    },
    {
      q: "Do you offer shuttles for conferences and seminars?",
      a: "Yes, we move speakers and attendees between hotels, convention centres and seminar venues, with group coordination and timing set around the programme.",
    },
    {
      q: "Is corporate invoicing available?",
      a: "Yes. We offer clear corporate invoicing, suited to recurring business travel, executive transfers and the park's event services.",
    },
    {
      q: "How much does a chauffeur on hire cost in Sophia Antipolis?",
      a: "Hourly hire starts at €240 for 3 hours in E-Class, €360 in V-Class (7 seats) and €450 in S-Class. A four-hour half day comes to €320, €480 or €600 on the same progression, and an eight-hour day runs from €600 to €1,100. The minimum is three hours; past the selected package, each extra hour costs between €80 and €150 depending on the class.",
    },
    {
      q: "Do you cover the Nice ⇄ Sophia Antipolis link?",
      a: "Yes, it is one of our most requested routes: about 25 minutes via the A8 from Nice airport, and around thirty from central Nice. It can be booked as a single transfer or folded into hourly hire when several appointments follow one another during the day.",
    },
  ],
  relatedLinks: [
    { label: "Chauffeur hire rates", href: "/en/chauffeur#mise-a-disposition" },
    { label: "Business chauffeur", href: "/en/chauffeur/business" },
    { label: "Private car service in Nice", href: "/en/chauffeur/nice" },
    {
      label: "Cannes ⇄ Nice Airport transfer",
      href: "/en/chauffeur/transfers/cannes-nice-airport",
    },
    {
      label: "Cannes ⇄ Monaco transfer",
      href: "/en/chauffeur/transfers/cannes-monaco",
    },
    { label: "All Riviera transfers", href: "/en/chauffeur/transfers" },
  ],
  cta: {
    title: "Your corporate chauffeur in Sophia Antipolis",
    text: "Executive transfer, multi-site meetings or conference shuttle: book your chauffeur in the technology park.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Private & corporate chauffeur in Sophia Antipolis",
    description:
      "Private and corporate chauffeur in Sophia Antipolis: hourly hire from €240, executive Nice airport transfers, multi-site meetings, corporate invoicing.",
    canonical: "/en/chauffeur/sophia-antipolis",
  },
};

export default sophiaAntipolis;
